import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { Crosshair, Loader2, Megaphone, Mail, Phone, X, MapPin, FilePlus2, ExternalLink, List, Map as MapIcon, FileImage } from 'lucide-react';
import { toast } from 'sonner';
import { NewCampaignV2Dialog } from './NewCampaignV2Dialog';
import { SearchHistory } from './SearchHistory';
import { BuyerMap } from './BuyerMap';
import { FlyerDialog } from '@/components/flyer/FlyerDialog';
import { buildFlyerText, fmtPriceShort } from '@/components/flyer/flyerUtils';

const parseTyped = (text) => {
  const parts = text.split(',').map(x => x.trim()).filter(Boolean);
  const zip = (text.match(/\b(\d{5})(?:-\d{4})?\b(?!.*\b\d{5}\b)/) || [])[1] || '';
  const state = (text.match(/\b([A-Z]{2})\b\s*\d{0,5}\s*$/) || [])[1] || '';
  const city = parts.length > 1 ? parts[1].replace(/\b[A-Z]{2}\b\s*\d*$/, '').replace(/\d{5}.*/, '').trim() : '';
  return { property_address: parts[0] || text, property_city: city, property_state: state, property_zip: zip };
};

const money = v => (v ? `$${Math.round(v).toLocaleString()}` : '—');
const scoreCls = s => (s >= 70 ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/40' : s >= 40 ? 'bg-amber-500/15 text-amber-300 ring-amber-500/40' : 'bg-slate-500/15 text-slate-300 ring-slate-500/30');

function ContactLines({ contacts }) {
  return (
    <div className="space-y-1">
      {contacts.slice(0, 2).map(c => (
        <div key={c.id} className="text-xs">
          <p className="font-medium text-gray-800">{[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Contact'}{c.title ? <span className="text-gray-500"> · {c.title}</span> : ''}</p>
          <p className="flex flex-wrap gap-x-3 text-gray-500">
            {(c.emails || []).filter(Boolean).slice(0, 1).map(e => <a key={e} href={`mailto:${e}`} className="flex items-center gap-1 hover:text-blue-300"><Mail className="h-3 w-3" />{e}</a>)}
            {(c.phones || []).filter(p => p.number).slice(0, 1).map(p => <a key={p.number} href={`tel:${p.number}`} className="flex items-center gap-1 hover:text-blue-300"><Phone className="h-3 w-3" />{p.number}{p.dnc ? ' (DNC)' : ''}</a>)}
          </p>
        </div>
      ))}
      {contacts.length > 2 && <p className="text-[10px] text-gray-500">+{contacts.length - 2} more</p>}
    </div>
  );
}

function ResultRow({ b, i, checked, onToggle, onOpen }) {
  return (
    <tr className="border-t border-gray-100 align-top hover:bg-gray-50" data-testid={`pm-row-${b.id}`}>
      <td className="px-3 py-3"><Checkbox data-testid={`pm-select-${b.id}`} checked={checked} onCheckedChange={onToggle} /></td>
      <td className="px-2 py-3 text-xs text-gray-500 tabular-nums">{i + 1}</td>
      <td className="px-3 py-3 min-w-[180px]">
        <button onClick={onOpen} className="text-left font-semibold text-gray-900 hover:text-blue-300" data-testid={`pm-open-${b.id}`}>{b.entity_name}</button>
        <p className="text-[11px] text-gray-500">{b.tier} · {b.total_purchases_24mo || 0} buys/24mo · {money(b.min_price)}–{money(b.max_price)}</p>
      </td>
      <td className="px-3 py-3 space-y-1">
        <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-bold tabular-nums ring-1 ${scoreCls(b.score)}`} data-testid={`pm-score-${b.id}`}>{b.score}</span>
        {b.nearest && <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-cyan-300" title={b.nearest.address} data-testid={`pm-distance-${b.id}`}><MapPin className="h-3 w-3" />{b.nearest.miles.toFixed(1)} mi</span>}
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-[11px] text-gray-500 max-w-[220px]">{(b.reasons || []).join(' · ')}</td>
      <td className="px-3 py-3 min-w-[220px]"><ContactLines contacts={b.contacts} /></td>
    </tr>
  );
}

export function PropertyMatchBar() {
  const navigate = useNavigate();
  const [prop, setProp] = useState({ property_address: '', property_type: 'Single Family' });
  const [price, setPrice] = useState('');
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const ids = Object.keys(sel).filter(k => sel[k]);

  const [lead, setLead] = useState(null);
  const onPlace = p => setProp(x => ({ ...x, property_address: p.street, property_city: p.city, property_state: p.state, property_zip: p.zip, property_county: p.county, lat: p.lat, lng: p.lng }));
  const [view, setView] = useState('list');
  const [flyerOpen, setFlyerOpen] = useState(false);
  const [flyerUse, setFlyerUse] = useState(null);
  const openCampaign = async (use) => {
    let u = use;
    if (!u) {
      const params = lead ? { lead_id: lead.id, address: res.property.property_address } : { address: res.property.property_address };
      const f = await API.get('/flyers/lookup', { params }).then(r => r.data.flyer).catch(() => null);
      u = f ? { flyer: f, text: buildFlyerText({ ...f, photo_count: f.photos?.length || 0 }, f.token) } : null;
    }
    setFlyerUse(u); setOpen(true);
  };
  const [histKey, setHistKey] = useState(0);
  const run = async (q, askingPrice) => {
    setBusy(true);
    try {
      const r = await API.post('/buyer-match/property', { ...q, asking_price: askingPrice, limit: 20 });
      setRes(r.data); setLead(null); setSel(Object.fromEntries(r.data.items.map(b => [b.id, true]))); setHistKey(k => k + 1);
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); return null; }
    finally { setBusy(false); }
  };
  const find = async () => {
    const q = prop.property_zip || prop.property_city ? prop : { ...prop, ...parseTyped(prop.property_address) };
    if (!q.property_zip && !q.property_city) return toast.error('Pick an address from the suggestions, or type it as "street, city, FL zip"');
    setProp(q);
    await run(q, Number(String(price).replace(/[^\d.]/g, '')) || 0);
  };
  const loadSaved = (p) => {
    const { asking_price, ...rest } = p;
    setProp(rest); setPrice(asking_price ? String(asking_price) : ''); setInputKey(k => k + 1);
    return run(rest, asking_price || 0);
  };
  const reblast = async (p) => {
    const d = await loadSaved(p);
    if (d?.items.length) openCampaign(); else if (d) toast.info('No buyers with contact info for this property right now');
  };
  const clear = () => { setLead(null); setRes(null); setSel({}); setProp({ property_address: '', property_type: 'Single Family' }); setPrice(''); setInputKey(k => k + 1); };
  const allOn = res?.items.length > 0 && ids.length === res.items.length;
  const [savingLead, setSavingLead] = useState(false);
  const saveLead = async () => {
    const p = res.property;
    setSavingLead(true);
    try {
      const r = await API.post('/leads', { name: p.property_address, status: 'Marketed', property_address: p.property_address, property_city: p.property_city, property_state: p.property_state,
        property_zip: p.property_zip, county: p.property_county, property_type: p.property_type, marketing_price: p.asking_price || 0 });
      setLead(r.data); toast.success('Saved to your pipeline as Marketed');
    } catch (e) {
      if (e.response?.status === 409) {
        const all = await API.get('/leads').catch(() => ({ data: [] }));
        const ex = all.data.find(l => (l.property_address || '').toLowerCase() === p.property_address.toLowerCase());
        if (ex) { setLead(ex); toast.info('This property is already a lead — campaigns will link to it'); }
        else toast.error(e.response.data.detail);
      } else toast.error(e.response?.data?.detail || 'Could not save lead');
    }
    setSavingLead(false);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3" data-testid="property-match-bar">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500"><Crosshair className="h-3.5 w-3.5 text-[#0073ea]" /> Find buyers for a property</p>
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[260px]">
          <AddressAutocomplete key={inputKey} value={prop.property_address} onChange={v => setProp(x => ({ property_type: x.property_type, property_address: v }))} onPlaceSelect={onPlace}
            placeholder="Start typing the property address…" className="h-10 bg-white border-gray-300" data-testid="pm-address" />
        </div>
        <Input data-testid="pm-price" value={price} onChange={e => setPrice(e.target.value)} placeholder="Sale price" className="h-10 flex-1 min-w-[140px] sm:flex-none sm:w-44 bg-white border-gray-300" inputMode="numeric" />
        <Select value={prop.property_type} onValueChange={v => setProp(x => ({ ...x, property_type: v }))}>
          <SelectTrigger data-testid="pm-type" className="h-10 flex-1 min-w-[140px] sm:flex-none sm:w-40 bg-white border-gray-300"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="Single Family">Single Family</SelectItem><SelectItem value="Multifamily">Multifamily</SelectItem></SelectContent>
        </Select>
        <Button data-testid="pm-find-btn" onClick={find} disabled={busy} className="h-10 bg-[#0073ea] hover:bg-[#0060c2] text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Crosshair className="h-4 w-4 mr-1.5" />}Find Top 20 Buyers
        </Button>
      </div>
      <SearchHistory refreshKey={histKey} onRerun={loadSaved} onReblast={reblast} />
      {prop.property_zip || prop.property_city ? <p className="text-[11px] text-gray-500" data-testid="pm-location">{[prop.property_city, prop.property_state, prop.property_zip].filter(Boolean).join(', ')}{prop.property_county ? ` · ${prop.property_county} County` : ''}</p> : null}
      {res && (
        <div className="space-y-2" data-testid="pm-results">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {res.pending_geocode > 0 && <p className="w-full text-[11px] text-amber-300" data-testid="pm-geocode-pending">Mapping {res.pending_geocode} purchase addresses for distance ranking — search again in a minute for full results.</p>}
            {!res.geocoded && <p className="w-full text-[11px] text-gray-500" data-testid="pm-no-geocode">Couldn't place this address on the map, so buyers are ranked without distance.</p>}
            <p className="text-sm text-gray-700" data-testid="pm-count"><b>{res.total}</b> buyer{res.total === 1 ? '' : 's'} with contact info for {res.property.property_address || 'this property'}{ids.length ? ` · ${ids.length} selected` : ''}</p>
            <div className="flex flex-wrap gap-2">
              <div className="flex rounded-md border border-gray-300 p-0.5" data-testid="pm-view-toggle">
                {[['list', List, 'List'], ['map', MapIcon, 'Map']].map(([k, I, l]) => (
                  <button key={k} onClick={() => setView(k)} data-testid={`pm-view-${k}`} className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${view === k ? 'bg-blue-50 text-[#0073ea]' : 'text-gray-500 hover:text-gray-900'}`}><I className="h-3.5 w-3.5" />{l}</button>
                ))}
              </div>
              {lead
                ? <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${lead.id}`)} data-testid="pm-open-lead-btn"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open Lead</Button>
                : <Button size="sm" variant="outline" onClick={saveLead} disabled={savingLead} data-testid="pm-save-lead-btn"><FilePlus2 className="h-3.5 w-3.5 mr-1" />Save as Lead</Button>}
              <Button size="sm" variant="outline" onClick={clear} data-testid="pm-clear-btn"><X className="h-3.5 w-3.5 mr-1" />Clear</Button>
              <Button size="sm" variant="outline" onClick={() => setFlyerOpen(true)} data-testid="pm-flyer-btn" className="border-violet-400/50 text-violet-300"><FileImage className="h-3.5 w-3.5 mr-1" />Deal Flyer</Button>
              <Button size="sm" disabled={!ids.length} onClick={() => openCampaign()} data-testid="pm-create-campaign-btn" className="bg-[#0073ea] hover:bg-[#0060c2] text-white"><Megaphone className="h-3.5 w-3.5 mr-1" />Create Campaign ({ids.length})</Button>
            </div>
          </div>
          {res.items.length === 0 ? <p className="py-6 text-center text-sm text-gray-500" data-testid="pm-empty">No buyers with contact info buy near this property yet.</p> : view === 'map' ? (
            <BuyerMap point={res.point} pins={res.pins || []} items={res.items} onOpenBuyer={id => navigate(`/buyers/${id}`)} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-2"><Checkbox data-testid="pm-select-all" checked={allOn} onCheckedChange={v => setSel(Object.fromEntries(res.items.map(b => [b.id, !!v])))} /></th>
                  <th className="px-2 py-2">#</th><th className="px-3 py-2">Buyer</th><th className="px-3 py-2">Score</th><th className="hidden md:table-cell px-3 py-2">Why</th><th className="px-3 py-2">Contact</th>
                </tr></thead>
                <tbody>{res.items.map((b, i) => <ResultRow key={b.id} b={b} i={i} checked={!!sel[b.id]} onToggle={v => setSel(s => ({ ...s, [b.id]: !!v }))} onOpen={() => navigate(`/buyers/${b.id}`)} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <NewCampaignV2Dialog open={open} onOpenChange={setOpen} preset={{
        ...(lead ? { buyer_ids: ids, lead_id: lead.id, name: `${lead.property_address} — top matches`, simpleBody: true } : { buyer_ids: ids, property: res?.property }),
        channel: 'email',
        ...(flyerUse ? { flyer_id: flyerUse.flyer.id, body: flyerUse.text, subject: `🔥 Off-market: ${flyerUse.flyer.property_address}${flyerUse.flyer.asking_price ? ` — ${fmtPriceShort(flyerUse.flyer.asking_price)}` : ''}` } : {}),
      }} />
      {res && <FlyerDialog open={flyerOpen} onOpenChange={setFlyerOpen} property={res.property} lead={lead} onUseInCampaign={u => (ids.length ? openCampaign(u) : toast.info('Select at least one buyer first'))} />}
    </div>
  );
}
